import moment from 'moment';
import React, { useEffect, useReducer, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { FaHistory } from 'react-icons/fa';
import CreditUpdatesService from '../../services/creditUpdates.service';
import DebtUpdatesService from '../../services/debtUpdates.service';
import HistoryLogsService from '../../services/historyLog.service';
import { getFieldLabel } from '../../constants/historyTranslations';
import Pill from '../EntityList/Pill';
import HistoryList from './HistoryList';
import './HistoryModal.css';

const CHILD_TAB_CONFIG = {
	debt: { title: 'Cuotas' },
	credit: { title: 'Cuotas' },
};

const isEmptyValue = (value) => value === undefined || value === null || value === '';

function mapHistoryLog(change, { showRecordLabel = false } = {}) {
	const base = {
		id: change._id || change.id,
		user: change.responsable ? change.responsable.name : '',
		date: moment.utc(change.createdAt).format('DD-MM-YYYY'),
		time: moment.utc(change.createdAt).format('HH:mm'),
		recordLabel: showRecordLabel ? change.recordLabel : null,
	};
	if (change.operacion === 'insert') {
		return { ...base, type: 'created', changes: [] };
	}
	if (change.operacion === 'delete') {
		return { ...base, type: 'deleted', changes: [] };
	}
	return {
		...base,
		type: 'modified',
		changes: (change.cambios || []).map((cambio) => ({
			field: getFieldLabel(change.modelo, cambio.campo),
			from: isEmptyValue(cambio.valorAnterior) ? '—' : (cambio.valorAnteriorDisplay || 'N/A'),
			to: cambio.valorNuevoDisplay || 'N/A',
		})),
	};
}

function mapDateChange(change, model) {
	return {
		id: change._id || change.id,
		type: 'modified',
		user: change.updatedDateBy ? change.updatedDateBy.name : '',
		date: moment.utc(change.createdAt).format('DD-MM-YYYY'),
		time: moment.utc(change.createdAt).format('HH:mm'),
		changes: [
			{
				field: getFieldLabel(model, 'firstDueExpiration'),
				from: moment.utc(change.firstDueExpirationBefore).format('DD-MM-YYYY'),
				to: moment.utc(change.firstDueExpirationUpdated).format('DD-MM-YYYY'),
			},
		],
		option: change.updateDateOption,
	};
}

const reducer = (state, action) => {
	switch (action.type) {
	case 'FETCH_DEBTUPDATES_REQUEST':
		return { ...state, loadingDebt: true };
	case 'FETCH_DEBTUPDATES_SUCCESS':
		return { ...state, debtUpdates: action.payload, loadingDebt: false };
	case 'FETCH_DEBTUPDATES_FAIL':
		return { ...state, loadingDebt: false, error: action.payload };
	case 'FETCH_CREDITUPDATES_REQUEST':
		return { ...state, loadingCredit: true };
	case 'FETCH_CREDITUPDATES_SUCCESS':
		return { ...state, creditUpdates: action.payload, loadingCredit: false };
	case 'FETCH_CREDITUPDATES_FAIL':
		return { ...state, loadingCredit: false, error: action.payload };
	case 'FETCH_HISTORYLOG_REQUEST':
		return { ...state, loading: true };
	case 'FETCH_HISTORYLOG_SUCCESS':
		return { ...state, historyLogs: action.payload, loading: false };
	case 'FETCH_HISTORYLOG_FAIL':
		return { ...state, loading: false, error: action.payload };
	case 'FETCH_CHILD_HISTORYLOG_REQUEST':
		return { ...state, loadingChild: true };
	case 'FETCH_CHILD_HISTORYLOG_SUCCESS':
		return { ...state, childLogs: action.payload.logs, childTrackingEnabled: action.payload.enabled, loadingChild: false };
	case 'FETCH_CHILD_HISTORYLOG_FAIL':
		return { ...state, loadingChild: false, error: action.payload };
	default:
		return state;
	}
};

function HistoryModal({ show, handleClose, model, id, isHistory }) {
	const [{ error, loading, loadingChild, loadingCredit, loadingDebt, debtUpdates, creditUpdates, historyLogs, childLogs, childTrackingEnabled }, dispatch] = useReducer(
		reducer,
		{
			debtUpdates: [],
			creditUpdates: [],
			historyLogs: [],
			childLogs: [],
			childTrackingEnabled: false,
			loading: false,
			loadingChild: false,
			loadingCredit: false,
			loadingDebt: false,
			error: '',
		},
	);
	const [activeTab, setActiveTab] = useState('changes');

	useEffect(() => {
		if (id) {
			const fetchData = async () => {
				if (model === 'debt') {
					dispatch({ type: 'FETCH_DEBTUPDATES_REQUEST' });
					try {
						const result = await DebtUpdatesService.listFromDebtId(id);
						dispatch({ type: 'FETCH_DEBTUPDATES_SUCCESS', payload: result.data });
					} catch (error) {
						dispatch({ type: 'FETCH_DEBTUPDATES_FAIL', payload: error.message });
					}
				}
				if (model === 'credit') {
					dispatch({ type: 'FETCH_CREDITUPDATES_REQUEST' });
					try {
						const result = await CreditUpdatesService.listFromCreditId(id);
						dispatch({ type: 'FETCH_CREDITUPDATES_SUCCESS', payload: result.data });
					} catch (error) {
						dispatch({ type: 'FETCH_CREDITUPDATES_FAIL', payload: error.message });
					}
				}
			};
			const fetchHistoryData = async () => {
				dispatch({ type: 'FETCH_HISTORYLOG_REQUEST' });
				try {
					const result = await HistoryLogsService.listFromId(id);
					dispatch({ type: 'FETCH_HISTORYLOG_SUCCESS', payload: result.data });
				} catch (error) {
					dispatch({ type: 'FETCH_HISTORYLOG_FAIL', payload: error.message });
				}
			};
			if (isHistory) {
				fetchHistoryData();
			} else {
				fetchData();
			}
		}
	}, [id, isHistory, model]);

	useEffect(() => {
		if (id && isHistory && CHILD_TAB_CONFIG[model]) {
			const fetchChildHistoryData = async () => {
				dispatch({ type: 'FETCH_CHILD_HISTORYLOG_REQUEST' });
				try {
					const result = await HistoryLogsService.listChildrenFromId(model, id);
					dispatch({ type: 'FETCH_CHILD_HISTORYLOG_SUCCESS', payload: result.data });
				} catch (error) {
					dispatch({ type: 'FETCH_CHILD_HISTORYLOG_FAIL', payload: error.message });
				}
			};
			fetchChildHistoryData();
		}
	}, [id, isHistory, model]);

	const updates = model === 'debt' ? debtUpdates : model === 'credit' ? creditUpdates : [];
	const childTabConfig = CHILD_TAB_CONFIG[model];
	const showTabs = isHistory && childTabConfig && childTrackingEnabled;
	const isLoading = loading || loadingChild || loadingCredit || loadingDebt;

	let body;
	if (!isHistory) {
		body = (
			<HistoryList
				loading={isLoading}
				error={error}
				items={updates.map((change) => mapDateChange(change, model))}
				emptyMessage='No hay cambios de fecha registrados'
			/>
		);
	} else if (showTabs) {
		const mappedChanges = historyLogs.map((change) => mapHistoryLog(change));
		const mappedChildren = childLogs.map((change) => mapHistoryLog(change, { showRecordLabel: true }));
		body = (
			<>
				<div className='hist-tabs'>
					<button
						type='button'
						className={`hist-tab${activeTab === 'changes' ? ' active' : ''}`}
						onClick={() => setActiveTab('changes')}
					>
						Cambios <Pill tone={activeTab === 'changes' ? 'accent' : 'neutral'}>{mappedChanges.length}</Pill>
					</button>
					<button
						type='button'
						className={`hist-tab${activeTab === 'children' ? ' active' : ''}`}
						onClick={() => setActiveTab('children')}
					>
						{childTabConfig.title} <Pill tone={activeTab === 'children' ? 'accent' : 'neutral'}>{mappedChildren.length}</Pill>
					</button>
				</div>
				{activeTab === 'changes' ? (
					<HistoryList loading={loading} error={error} items={mappedChanges} emptyMessage='No hay cambios registrados' />
				) : (
					<HistoryList loading={loadingChild} error={error} items={mappedChildren} emptyMessage='No hay cambios registrados en las cuotas' />
				)}
			</>
		);
	} else {
		body = (
			<HistoryList
				loading={isLoading}
				error={error}
				items={historyLogs.map((change) => mapHistoryLog(change))}
				emptyMessage='No hay cambios registrados'
			/>
		);
	}

	return (
		<Modal show={show} onHide={handleClose} size='md' dialogClassName='hist-modal' contentClassName='hist-modal-content'>
			<Modal.Header closeButton={!isLoading}>
				<div className='hist-modal-header-inner'>
					<div className='hist-modal-icon'><FaHistory size={18} /></div>
					<Modal.Title>Historial de cambios</Modal.Title>
				</div>
			</Modal.Header>
			<Modal.Body className='hist-modal-body'>
				{body}
			</Modal.Body>
			<Modal.Footer className='hist-modal-footer'>
				<Button variant='secondary' className='hist-close-btn' onClick={handleClose}>Cerrar historial</Button>
			</Modal.Footer>
		</Modal>
	);
}

export default HistoryModal;
